/* eslint-disable react/no-unknown-property */
import type { ThreeEvent, Vector3 } from '@react-three/fiber'
import React from 'react'
import * as THREE from 'three'

export interface ControlOptions {
  id?: string
  label?: string
  actionTarget?: string
  action?: 'redirectBuilding' | 'changeScene'
  hoverColor?: string
  hoverActive?: boolean
  position?: number[]
  rotation?: number[]
  scale?: number
  onClick?: (arg: any) => void
}

export interface ModelControlsInterface {
  [key: string]: ControlOptions
}

export type INTERACTION_HOVER = 'hover'
export type INTERACTION_POINT = 'point'

type TBoxGeometry =
  | [
      width?: number | undefined,
      height?: number | undefined,
      depth?: number | undefined,
      widthSegments?: number | undefined,
      heightSegments?: number | undefined,
      depthSegments?: number | undefined,
    ]
  | undefined

interface HoverHitBoxInterface {
  node: THREE.Mesh
  handleOnDoubleClick: (e: ThreeEvent<MouseEvent>) => void
  handleOnPointerOver: (e: ThreeEvent<PointerEvent>) => void
  handleOnPointerOut: (e: ThreeEvent<PointerEvent>) => void
  materialColor?: string
  isVisible?: boolean
  interactionMode?: INTERACTION_HOVER | INTERACTION_POINT
}

export const getNodeMeshSize = (nodeMesh: THREE.Mesh) => {
  const padding = new THREE.Vector3(1.0, 0, 1.0)
  const box = new THREE.Box3().setFromObject(nodeMesh.children[0])
  box.expandByVector(padding)
  const boxSize = box.getSize(new THREE.Vector3())
  return [boxSize?.x || 0, boxSize?.y || 0, boxSize?.z] as TBoxGeometry
}

export const getNodeMeshCenter = (nodeMesh: THREE.Mesh) => {
  const box = new THREE.Box3().setFromObject(nodeMesh.children[0])
  const center = box.getCenter(new THREE.Vector3())
  return [center?.x || 0, center?.y || 0, center?.z] as Vector3
}

function HoverHitBox({
  node,
  handleOnDoubleClick,
  handleOnPointerOver,
  handleOnPointerOut,
  materialColor = '',
  isVisible = false,
  interactionMode = 'hover',
}: HoverHitBoxInterface) {
  const hitBoxRef = React.useRef<THREE.Mesh>(null)

  React.useEffect(() => {
    if (hitBoxRef.current && materialColor) {
      const obj = hitBoxRef.current
      const objMaterial = obj.material as THREE.MeshStandardMaterial
      obj.visible = isVisible ?? false
      objMaterial.color.set(materialColor)
      objMaterial.transparent = true
      objMaterial.opacity = 0.7
      objMaterial.needsUpdate = true
    }
  }, [hitBoxRef, isVisible, materialColor])

  if (interactionMode === 'hover') {
    return (
      <mesh
        ref={hitBoxRef}
        visible={false}
        key={`hitbox-mesh-${node.name}`}
        name={node.children[0].name}
        position={getNodeMeshCenter(node)}
        onDoubleClick={(e) => handleOnDoubleClick(e)}
        onPointerOver={(e) => handleOnPointerOver(e)}
        onPointerOut={(e) => handleOnPointerOut(e)}
      >
        <boxGeometry args={getNodeMeshSize(node)} />
      </mesh>
    )
  }

  return (
    <mesh
      ref={hitBoxRef}
      visible={false}
      key={`hitbox-mesh-${node.name}`}
      name={node.children[0].name}
      position={getNodeMeshCenter(node)}
      onDoubleClick={(e) => handleOnDoubleClick(e)}
      onPointerDown={(e) => handleOnPointerOver(e)}
      onPointerUp={(e) => handleOnPointerOut(e)}
      onPointerOut={(e) => handleOnPointerOut(e)}
    >
      <boxGeometry args={getNodeMeshSize(node)} />
    </mesh>
  )
}

export default HoverHitBox
